import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  Box,
  CircularProgress,
} from '@mui/material';

export interface PriceAdjustmentValues {
  priceAdjustmentDate?: string;
  priceAdjustmentPercentage?: number;
}

interface ProviderPriceAdjustmentDialogProps {
  open: boolean;
  /** Data atual do reajuste no formato 'YYYY-MM-DD' (ou vazio). */
  initialDate: string;
  /** Percentual atual como string (ou vazio). */
  initialPercentage: string;
  saving: boolean;
  onClose: () => void;
  onSave: (values: PriceAdjustmentValues) => void;
}

/**
 * Modal acessível pela tela de vínculo de exames que edita os campos de reajuste
 * (Data do Reajuste e Reajuste de Exames %) salvos no cadastro do prestador.
 */
const ProviderPriceAdjustmentDialog: React.FC<ProviderPriceAdjustmentDialogProps> = ({
  open,
  initialDate,
  initialPercentage,
  saving,
  onClose,
  onSave,
}) => {
  const [date, setDate] = useState(initialDate);
  const [percentage, setPercentage] = useState(initialPercentage);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDate(initialDate);
      setPercentage(initialPercentage);
      setError(null);
    }
  }, [open, initialDate, initialPercentage]);

  const handleSave = () => {
    const values: PriceAdjustmentValues = {};
    if (date) {
      values.priceAdjustmentDate = date;
    }
    if (percentage !== '') {
      const pct = Number(percentage);
      if (!Number.isFinite(pct) || pct < 0) {
        setError('Percentual deve ser um número maior ou igual a zero');
        return;
      }
      values.priceAdjustmentPercentage = pct;
    }
    onSave(values);
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Reajuste de valores dos exames</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                label="Data do Reajuste"
                type="date"
                value={date}
                onChange={(e) => { setDate(e.target.value); setError(null); }}
                fullWidth
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label="Reajuste de Exames (%)"
                type="number"
                value={percentage}
                onChange={(e) => { setPercentage(e.target.value); setError(null); }}
                fullWidth
                placeholder="0,00"
              />
            </Grid>
            {error && (
              <Grid size={12}>
                <Alert severity="error">{error}</Alert>
              </Grid>
            )}
            <Grid size={12}>
              <Alert severity="info">
                Estes valores são salvos no cadastro do prestador. A aplicação
                efetiva do percentual aos preços será feita posteriormente.
              </Alert>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? 'Salvando...' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProviderPriceAdjustmentDialog;
